
import Vue, { PropType, VNode, VNodeChildren } from 'vue';
import { VTextarea } from 'vuetify/lib';
import { QuillOptionsStatic, RangeStatic, Sources } from 'quill';
import { merge } from 'lodash';
import mixins from '@/utils/mixins';

import QuillToolbarVue from './QuillToolbar.vue';
import Quill from 'quill';

import QuillMentionsMixin from '@/mixins/quill/mention-quill';

export type ToolbarOptions = typeof toolbarOptions;
export const toolbarOptions = {
  essential: [
    { size: ['small', false, 'large', 'huge'] },
    'bold',
    { color: [] }
  ]
};

const baseMixins = mixins(VTextarea, QuillMentionsMixin);
interface Options extends InstanceType<typeof baseMixins> {
  // inherited from VTextArea
  disabled: boolean;
  readonly: boolean;
  isFocused: boolean;
  isDisabled: boolean;
  lazyValue: string;
  internalValue: string;
  _uid: number;
  setLabelWidth(): void;
  setPrefixWidth(): void;
  setPrependWidth(): void;

  genLabel(): VNode;
  genAffix(affix: string): VNode;
  prefix: string;
  suffix: string;

  $refs: {
    editor: HTMLElement;
    menu: Vue & {
      activeTile?: HTMLElement;
      listIndex: number;
      changeListIndex(e: KeyboardEvent): void;
    };
  };
}

export default baseMixins
  .extend<Options>({ functional: false })
  .extend({
    name: 'QuillEditor',

    props: {
      value: String as PropType<string>,

      placeholder: String,

      options: {
        type: Object as PropType<QuillOptionsStatic>,
        default: () => ({})
      },

      dense: { type: Boolean, default: true },
      outlined: { type: Boolean, default: true },

      toolbarFormats: {
        type: Array as PropType<string[]>,
        default: () => ['bold', 'italic', 'underline', 'link']
      },

      type: {
        type: String as PropType<'field' | 'area'>,
        default: 'area'
      },

      toolbar: {
        type: [Array] as PropType<any[] | string[]>,
        required: false
      }
    },

    data() {
      return {
        quill: null as Quill | null,

        defaultOptions: {
          modules: {
            table: true
          },
          bounds: '.quill-editor',
          formats: [
            'bold',
            'italic',
            'link',
            'underline',
            'header',
            'indent',
            'list',
            'align',
            'direction'
          ]
        } as QuillOptionsStatic
      };
    },

    computed: {
      classes(): object {
        return {
          'v-text-wysiwyg': true,
          'v-text-wysiwyg-area': this.type == 'area',
          'v-text-wysiwyg-field': this.type == 'field',
          ...(VTextarea as any).options.computed.classes.call(this)
        };
      },

      editor(): Element | undefined {
        return this.$refs.editor;
      },

      uniqueToolbarId() {
        return `quill-toolbar-${this._uid}`;
      }
    },

    mounted() {
      this.initialize();
    },

    beforeDestroy() {
      // TODO: LH - 2023-10-02 - Check why event handler seems to be called a number of times
      this.removeQuillEventHandlers();
      this.quill = null;
    },

    watch: {
      value: {
        async handler(newContent, oldContent) {
          if (!this.quill || this.isContentSame(this.getHTML())) {
            return;
          }

          // Restore the selection and cursor position after updating the content
          const selection = this.quill.getSelection();

          if (selection) {
            await Vue.nextTick();
            this.quill.setSelection(selection);
          }

          this.setContents(newContent);
        },
        deep: true
      },

      disabled: {
        handler(enable: boolean) {
          if (this.quill) this.quill.enable(!enable);
        }
      },

      isFocused: {
        handler(focus) {
          if (focus) this.$emit('focus', this.editor);
          else this.$emit('blur', this.editor);
        }
      }
    },

    methods: {
      /**
       * Option passed into the editor to be used when initialising vue.
       */
      composeOptions(): QuillOptionsStatic {
        const clientOptions: QuillOptionsStatic = this.options;
        // theme of quill
        clientOptions.theme = 'snow';
        clientOptions.readOnly = this.readonly;
        clientOptions.placeholder = this.placeholder;

        const options = merge(this.defaultOptions, {
          ...clientOptions,
          modules: {
            // using the default bindings for keyboard if mentions are not supported.
            keyboard: this.mentions
              ? { bindings: this.keyboardBindings() }
              : {},
            // to avoid use of only one toolbar assign unique id for each toolbar
            // https://github.com/quilljs/quill/issues/633
            toolbar: this.toolbar
              ? (() => {
                  return this.toolbar;
                })()
              : `#${this.uniqueToolbarId}`
          }
        });

        return options;
      },

      initialize() {
        if (!this.editor) return;

        const options = this.composeOptions();

        // Create new Quill instance
        this.quill = new Quill(this.editor, options);

        // Set editor content
        this.setContents(this.value);

        // Set event handlers
        this.quill.on('text-change', this.handleTextChange);
        this.quill.on('selection-change', this.handleSelectionChange);

        // Disabled editor
        if (!this.disabled) {
          this.quill.enable(true);
        }

        // Emit ready event
        this.$emit('ready', this.quill);

        const toolbar = this.quill.getModule('toolbar');

        toolbar.addHandler('image', () => {
          let fileInput = (this.quill! as Quill & {
            container: HTMLDivElement;
          }).container.querySelector<HTMLInputElement>(
            'input.ql-image[type=file]'
          );

          if (fileInput == null) {
            fileInput = document.createElement('input');
            fileInput.setAttribute('type', 'file');
            fileInput.setAttribute('multiple', 'true');

            fileInput.classList.add('ql-image', 'ql-picker', 'd-none');
            fileInput.addEventListener('change', $event => {
              this.$nextTick(() => {
                const files = Array.from(($event.target as any).files);
                this.$emit('images-added', files);
              });

              // Wait for next frame to remove the current list of files
              this.$nextTick(() => {
                fileInput!.value = '';
              });
            });

            (this.quill! as Quill & {
              container: HTMLDivElement;
            }).container.appendChild(fileInput);
          }

          if (fileInput) {
            fileInput.click();
          }
        });
      },

      removeQuillEventHandlers() {
        // Remove event handlers before setting quill to null
        this.quill!.off('text-change', this.handleTextChange);
        this.quill!.off('selection-change', this.handleSelectionChange);
      },

      setContents(content: string) {
        const normalizedContent = !content ? '' : content;
        this.setHTML(normalizedContent);

        this.lazyValue = normalizedContent;
      },

      getHTML(): string {
        return this.quill!.root.innerHTML ?? '';
      },

      setHTML(html: string) {
        if (this.quill) this.quill.root.innerHTML = html;
      },

      isContentSame(content: string) {
        return content === this.lazyValue;
      },

      handleTextChange(delta: any, oldContents: any, _source: Sources) {
        this.lazyValue = this.getHTML();
        // Update v-model when content changes
        if (!this.isContentSame(this.value)) {
          this.internalValue = this.lazyValue;
        }

        if (this.mentions) {
          this.$nextTick(() => {
            this.mentionTextHandler();
          });
        }

        this.$emit('text-change', this.lazyValue);
      },

      handleSelectionChange(
        range: RangeStatic | null,
        oldRange: RangeStatic | null,
        source: Sources
      ) {
        // Set isFocused if quill.hasFocus()
        this.isFocused = !!this.quill!.hasFocus();
        this.$emit('selection-change', { range, oldRange, source });
        if (this.mentions) {
          if (range && range.length === 0) {
            this.mentionTextHandler();
          } else {
            this.closeMentionMenu();
          }
        }
      },

      // COMPONENTS
      /**
       * Update genTextFieldSlot to have menu as a child to be rendered.
       */
      genTextFieldSlot() {
        const children: VNodeChildren = [this.genLabel()];
        // insert prefix and label at the start of the slot
        if (this.prefix) {
          children.push(this.genAffix('prefix'));
        }
        children.push(this.genInput());

        // insert suffix and menu at the end of the slot
        if (this.mentions) {
          children.push(this.genMentionsMenu());
        }
        if (this.suffix) {
          children.push(this.genAffix('suffix'));
        }

        return this.$createElement(
          'div',
          {
            staticClass: 'v-text-field__slot'
          },
          children
        );
      },

      genInput() {
        const listeners = {
          keydown: this.mentions ? this.onKeyDown : () => {}
        };
        return this.$createElement(
          'div',
          {
            staticClass: 'quill-editor',
            class: 'd-flex',
            style: {
              'flex-direction': this.toolbar ? 'column-reverse' : 'column'
            },
            on: listeners
          },
          [
            this.$createElement('div', {
              ref: 'editor',
              ...this.$attrs,
              directives: [
                {
                  name: 'resize',
                  modifiers: { quiet: true },
                  value: this.onResize
                }
              ]
            }),
            this.$slots.default,
            this.toolbar ? null : this.genInputToolbar()
          ]
        );
      },

      genInputToolbar() {
        return this.$createElement(QuillToolbarVue, {
          attrs: {
            id: this.uniqueToolbarId
          },
          props: {
            formats: this.toolbarFormats
          },
          scopedSlots: this.$scopedSlots
        });
      },

      // Events
      onClick() {
        if (this.isFocused || this.isDisabled || !this.$refs.input) return;

        this.quill!.focus();
      },

      // override default functionality of mousedown
      onMouseDown(e: Event) {
        return;
      },

      onResize() {
        this.setLabelWidth();
        this.setPrefixWidth();
        this.setPrependWidth();
        if (this.mentions) {
          if (this.quill && this.isMenuActive) {
            this.setMenuMentionPosition();
          }
        }
      }
    }
  });
